import React, { useState } from 'react'
import { 
  Users, 
  Plus, 
  UserPlus, 
  Copy, 
  LogOut, 
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { createFamily, joinFamily, leaveFamily } from '../lib/supabase'

const FamilySection = ({ profile, onFamilyUpdate }) => {
  const [showCreateFamily, setShowCreateFamily] = useState(false)
  const [showJoinFamily, setShowJoinFamily] = useState(false)
  const [familyName, setFamilyName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('') // 'success' or 'error'

  const showMessage = (text, type) => {
    setMessage(text)
    setMessageType(type)
    setTimeout(() => {
      setMessage('')
      setMessageType('')
    }, 3000)
  }

  const handleCreateFamily = async (e) => {
    e.preventDefault()
    if (!familyName.trim()) return

    setLoading(true)
    try {
      await createFamily(familyName.trim(), profile.id)
      await onFamilyUpdate()
      setShowCreateFamily(false)
      setFamilyName('')
      showMessage('Familia creada exitosamente', 'success')
    } catch (error) {
      console.error('Error creando familia:', error)
      showMessage('Error al crear la familia', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinFamily = async (e) => {
    e.preventDefault()
    if (!inviteCode.trim()) return

    setLoading(true)
    try {
      await joinFamily(inviteCode.trim().toUpperCase(), profile.id)
      await onFamilyUpdate()
      setShowJoinFamily(false)
      setInviteCode('')
      showMessage('Te has unido a la familia exitosamente', 'success')
    } catch (error) {
      console.error('Error uniéndose a familia:', error)
      showMessage('Código de invitación inválido', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleLeaveFamily = async () => {
    if (!window.confirm('¿Estás seguro de que quieres abandonar la familia?')) return

    setLoading(true)
    try {
      await leaveFamily(profile.id)
      await onFamilyUpdate()
      showMessage('Has abandonado la familia', 'success')
    } catch (error) {
      console.error('Error abandonando familia:', error)
      showMessage('Error al abandonar la familia', 'error')
    } finally {
      setLoading(false)
    }
  }

  const copyInviteCode = () => {
    if (profile.families?.invite_code) {
      navigator.clipboard.writeText(profile.families.invite_code)
      showMessage('Código copiado al portapapeles', 'success')
    }
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <Users className="h-5 w-5" />
          <span>Gestión Familiar</span>
        </h3>
      </div>

      <div className="p-6">
        {/* Mensajes */}
        {message && (
          <div className={`mb-4 p-3 rounded-md flex items-center space-x-2 ${
            messageType === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {messageType === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span className="text-sm">{message}</span>
          </div>
        )}

        {profile.family_id ? (
          /* Usuario pertenece a una familia */
          <div className="space-y-4">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900">
                {profile.families?.name}
              </h4>
              <p className="text-sm text-gray-600">
                Compartes gastos con tu familia
              </p>
            </div>

            <div className="bg-gray-50 rounded-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Código de invitación
                  </p>
                  <p className="text-lg font-mono text-gray-900">
                    {profile.families?.invite_code}
                  </p>
                </div>
                <button
                  onClick={copyInviteCode}
                  className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Comparte este código para que otros se unan a tu familia
              </p>
            </div>

            <button
              onClick={handleLeaveFamily}
              disabled={loading}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <LogOut className="h-4 w-4" />
              <span>Abandonar Familia</span>
            </button>
          </div>
        ) : (
          /* Usuario no pertenece a ninguna familia */
          <div className="space-y-4">
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900">
                Sin familia
              </h4>
              <p className="text-sm text-gray-600">
                Crea o únete a una familia para compartir gastos
              </p>
            </div>

            {!showCreateFamily && !showJoinFamily && (
              <div className="space-y-3">
                <button
                  onClick={() => setShowCreateFamily(true)}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Crear Familia</span>
                </button>

                <button
                  onClick={() => setShowJoinFamily(true)}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Unirse a Familia</span>
                </button>
              </div>
            )}

            {/* Formulario crear familia */}
            {showCreateFamily && (
              <form onSubmit={handleCreateFamily} className="space-y-3">
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder="Nombre de la familia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateFamily(false)
                      setFamilyName('')
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Creando...' : 'Crear'}
                  </button>
                </div>
              </form>
            )}

            {/* Formulario unirse a familia */}
            {showJoinFamily && (
              <form onSubmit={handleJoinFamily} className="space-y-3">
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="Código de invitación"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                  required
                />
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowJoinFamily(false)
                      setInviteCode('')
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Uniéndose...' : 'Unirse'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FamilySection